import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useCallback,
} from "react";
import { User } from "../types";

// App State Interface
interface AppState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  theme: "light" | "dark";
  language: "tr" | "en";
  sidebarOpen: boolean;
  notifications: {
    unreadCount: number;
    lastChecked: Date | null;
  };
  globalError: string | null;
  globalSuccess: string | null;
}

// Action Types
type AppAction =
  | { type: "SET_USER"; payload: User | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_THEME"; payload: "light" | "dark" }
  | { type: "SET_LANGUAGE"; payload: "tr" | "en" }
  | { type: "TOGGLE_SIDEBAR" }
  | { type: "SET_SIDEBAR_OPEN"; payload: boolean }
  | {
      type: "UPDATE_NOTIFICATIONS";
      payload: { unreadCount: number; lastChecked?: Date };
    }
  | { type: "SET_GLOBAL_ERROR"; payload: string | null }
  | { type: "SET_GLOBAL_SUCCESS"; payload: string | null }
  | { type: "CLEAR_GLOBAL_MESSAGES" }
  | { type: "LOGOUT" };

// Initial State
const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  theme: (localStorage.getItem("theme") as "light" | "dark") || "light",
  language: (localStorage.getItem("language") as "tr" | "en") || "tr",
  sidebarOpen: false,
  notifications: {
    unreadCount: 0,
    lastChecked: null,
  },
  globalError: null,
  globalSuccess: null,
};

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case "SET_USER":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
      };

    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };

    case "SET_THEME":
      localStorage.setItem("theme", action.payload);
      return {
        ...state,
        theme: action.payload,
      };

    case "SET_LANGUAGE":
      localStorage.setItem("language", action.payload);
      return {
        ...state,
        language: action.payload,
      };

    case "TOGGLE_SIDEBAR":
      return {
        ...state,
        sidebarOpen: !state.sidebarOpen,
      };

    case "SET_SIDEBAR_OPEN":
      return {
        ...state,
        sidebarOpen: action.payload,
      };

    case "UPDATE_NOTIFICATIONS":
      return {
        ...state,
        notifications: {
          ...state.notifications,
          ...action.payload,
        },
      };

    case "SET_GLOBAL_ERROR":
      return {
        ...state,
        globalError: action.payload,
        globalSuccess: null, // Clear success message when showing error
      };

    case "SET_GLOBAL_SUCCESS":
      return {
        ...state,
        globalSuccess: action.payload,
        globalError: null, // Clear error message when showing success
      };

    case "CLEAR_GLOBAL_MESSAGES":
      return {
        ...state,
        globalError: null,
        globalSuccess: null,
      };

    case "LOGOUT":
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return {
        ...initialState,
        theme: state.theme,
        language: state.language,
      };

    default:
      return state;
  }
};

// Context Interface
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Convenience methods
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setTheme: (theme: "light" | "dark") => void;
  setLanguage: (language: "tr" | "en") => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  updateNotifications: (data: {
    unreadCount: number;
    lastChecked?: Date;
  }) => void;
  setGlobalError: (error: string | null) => void;
  setGlobalSuccess: (success: string | null) => void;
  clearGlobalMessages: () => void;
  logout: () => void;
}

// Create Context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider Component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Convenience methods
  const setUser = useCallback((user: User | null) => {
    dispatch({ type: "SET_USER", payload: user });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  }, []);

  const setTheme = useCallback((theme: "light" | "dark") => {
    dispatch({ type: "SET_THEME", payload: theme });
  }, []);

  const setLanguage = useCallback((language: "tr" | "en") => {
    dispatch({ type: "SET_LANGUAGE", payload: language });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: "TOGGLE_SIDEBAR" });
  }, []);

  const setSidebarOpen = useCallback((open: boolean) => {
    dispatch({ type: "SET_SIDEBAR_OPEN", payload: open });
  }, []);

  const updateNotifications = useCallback(
    (data: { unreadCount: number; lastChecked?: Date }) => {
      dispatch({ type: "UPDATE_NOTIFICATIONS", payload: data });
    },
    []
  );

  const setGlobalError = useCallback((error: string | null) => {
    dispatch({ type: "SET_GLOBAL_ERROR", payload: error });
  }, []);

  const setGlobalSuccess = useCallback((success: string | null) => {
    dispatch({ type: "SET_GLOBAL_SUCCESS", payload: success });
  }, []);

  const clearGlobalMessages = useCallback(() => {
    dispatch({ type: "CLEAR_GLOBAL_MESSAGES" });
  }, []);

  const logout = useCallback(() => {
    dispatch({ type: "LOGOUT" });
  }, []);

  const value: AppContextType = {
    state,
    dispatch,
    setUser,
    setLoading,
    setTheme,
    setLanguage,
    toggleSidebar,
    setSidebarOpen,
    updateNotifications,
    setGlobalError,
    setGlobalSuccess,
    clearGlobalMessages,
    logout,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Custom Hook
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};

// Selector hooks for better performance
export const useAppState = () => {
  const { state } = useApp();
  return state;
};

export const useUser = () => {
  const { state } = useApp();
  return { user: state.user, isAuthenticated: state.isAuthenticated };
};

export const useTheme = () => {
  const { state, setTheme } = useApp();
  return { theme: state.theme, setTheme };
};

export const useLanguage = () => {
  const { state, setLanguage } = useApp();
  return { language: state.language, setLanguage };
};

export const useSidebar = () => {
  const { state, toggleSidebar, setSidebarOpen } = useApp();
  return {
    sidebarOpen: state.sidebarOpen,
    toggleSidebar,
    setSidebarOpen,
  };
};

export const useNotifications = () => {
  const { state, updateNotifications } = useApp();
  return {
    notifications: state.notifications,
    updateNotifications,
  };
};

export const useGlobalMessages = () => {
  const { state, setGlobalError, setGlobalSuccess, clearGlobalMessages } =
    useApp();
  return {
    globalError: state.globalError,
    globalSuccess: state.globalSuccess,
    setGlobalError,
    setGlobalSuccess,
    clearGlobalMessages,
  };
};

export default AppContext;
